import React, { useCallback, useEffect, useState, useRef, ChangeEvent } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import moment from 'moment';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import IconButton from '@material-ui/core/IconButton';

// ICONS
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import { ReactComponent as HistogramHighActive } from '../../assets/images/icons/high-off-active.svg';
import { ReactComponent as HistogramLowActive } from '../../assets/images/icons/low-on-active.svg';
import { ReactComponent as ZoomIn } from '../../assets/images/icons/zoom-in.svg';
import { ReactComponent as ZoomOut } from '../../assets/images/icons/zoom-out.svg';
import { ReactComponent as DropdownArrowIcon } from '../../assets/images/icons/dropdown-arrow.svg';
import { Scrollbar } from 'react-scrollbars-custom';

import Player from '../../../../components/Dashboard/HMI/Player';

import { DashboardActions, ExportDataActions, FormActions, statesActions } from '../../../actions';
import { graphConstants } from '../../../constants';

import './styles/Panel.scss';
import DateRangePicker from './DateRangePicker';
import ButtonMenu from '../../../../components/Dashboard/HMI/ButtonMenu';
import { GraphActions } from '../../../../base/store/actions';
import { HmiPlayerActions } from '../../../actions';
import { IDashboard, IHmiSchema } from '../../../interfaces';
import { appConfig } from '../../../../config/appConfig';
import { RootState } from '../../../store';
import { selectMainIntervals } from '../../../selectors/appSetting/appSettingSelector';
import { delay } from 'lodash';
import { isMobile } from 'react-device-detect';
import PanelMenu from './PanelMenu';

interface IPanelProps {
    histogramHeight: number;
    histogramMode?: string;
    barHeight: number;
    barDescriptionVisible?: boolean;
    minimapVisible?: boolean;
    histogramToggleHeight: (status: boolean) => void;
    histogramSwitchMode: (name: string) => void;
    minimapToggle: (data: boolean) => void;
    togglePeriod: (name: string) => void;
    minimapPeriod: string;
    minimapRange: { startDate: Date; endDate: Date; key: string; };
    zoomSelection: (minimapRange: { startDate: Date; endDate: Date; key: string; }, zoom: string) => void;
    barToggleHeight: (statusHeight: boolean) => void;
    barToggleDescription: (data: boolean) => void;
    shiftSelection: (minimapRange: { startDate: Date; endDate: Date; key: string; }, position: string, minimapPeriod: string) => void;
    minimapScaleDates: Record<string, unknown>;
    deselectStates: () => void;
    dashboardOnline: boolean;
    screenWidth: number;
    selection: Date[];
    exportAllState: (from: string, to: string, fileName: string, dashboard: number)=> void;
    setOffCurrentTime: () => void;
    setOnCurrentTime: () => void;
    defaultMinimapPeriod: string;
    selectedDashboard: IDashboard;
    hmiVisible: boolean;
    toggleHMI: (isVisible: boolean) => void;
    realTimeHmiPlayer: (status: boolean) => void;
    setHmiSchema: (schema: IHmiSchema | null) => void;
    stopHmiPlayer: () => void;
    enableHmi: boolean;
    mainIntervals: {
        twoHours: boolean;
        twelveHours: boolean;
        day: boolean;
        week: boolean;
        month: boolean;
        year: boolean;
    };
}

/**
 * Getting data from the page session
 */
const pageReloaded = JSON.parse(window.sessionStorage.getItem('pageReloaded') || 'false');
const dashboardOnlineUrl = JSON.parse(window.sessionStorage.getItem('dashboardOnline') || 'false');
/**
 * A dashboard toolbar to manage graphics
 *
 * @param histogramHeight
 * @param histogramMode
 * @param barHeight
 * @param histogramToggleHeight
 * @param histogramSwitchMode
 * @param togglePeriod
 * @param minimapPeriod
 * @param minimapRange
 * @param zoomSelection
 * @param barToggleHeight
 * @param shiftSelection
 * @param deselectStates
 * @param dashboardOnline
 * @param screenWidth
 * @param selection
 * @param setOffCurrentTime
 * @param setOnCurrentTime
 * @param hmiVisible
 * @param toggleHMI
 * @param setHmiSchema
 * @param stopHmiPlayer
 *
 * @return {JSX.Element}
 *
 * @constructor
 */
const Panel: React.FC<IPanelProps> = (
    {
        histogramHeight = graphConstants.histogramSmallHeight,
        barHeight = graphConstants.barSmallHeight,
        histogramToggleHeight,
        histogramSwitchMode,
        togglePeriod,
        minimapPeriod,
        minimapRange,
        zoomSelection,
        barToggleHeight,
        shiftSelection,
        deselectStates,
        dashboardOnline,
        screenWidth,
        selection,
        setOffCurrentTime,
        realTimeHmiPlayer,
        setOnCurrentTime,
        defaultMinimapPeriod,
        hmiVisible = false,
        toggleHMI,
        setHmiSchema,
        stopHmiPlayer,
        enableHmi,
        mainIntervals,
    }: IPanelProps,
) => {

    const { t } = useTranslation();

    const histogramHeightSmall = histogramHeight === graphConstants.histogramSmallHeight,
        barHeightSmall = barHeight === graphConstants.barSmallHeight;
    const dispatch = useDispatch();

    const [hmiMenuAnchor, setHmiMenuAnchor] = useState<null | HTMLElement>(null);
    const hmiBtnRef = useRef<null | HTMLElement>(null);

    const [minimapPeriodSelected, setMinimapPeriodSelected] = useState<string>(appConfig.defaultMonitoringView);

    /**
     * Handle histogram height switch
     *
     */
    const histogramHeightSwitch = useCallback(() => {

        histogramToggleHeight(histogramHeightSmall);
        barHeightSwitch();

    }, [histogramToggleHeight, histogramHeightSmall]);

    useEffect(() => {

        setMinimapPeriodSelected(minimapPeriod);

    }, [minimapPeriod]);



    /**
     * Handle HMI visibility switch
     */
    const hmiSwitch = useCallback((event: ChangeEvent<HTMLElement>, checked: boolean) => {
        event.preventDefault();

        setHmiMenuAnchor(!hmiVisible ? hmiBtnRef.current : null);

        if (hmiVisible) {

            setHmiSchema(null);

            stopHmiPlayer();
        }

        delay(()=>toggleHMI(!checked), 0);

    }, [toggleHMI, hmiVisible, setHmiSchema]);

    /**
     * Handle bar chart height switch
     */
    const barHeightSwitch = useCallback(() => {

        barToggleHeight(barHeightSmall);

    }, [barToggleHeight, barHeightSmall]);


    /**
     * Setting the default period
     */
    useEffect(() => {

        if (pageReloaded || dashboardOnlineUrl) {

            togglePeriod(defaultMinimapPeriod);
        }

        setOnCurrentTime();

    }, [togglePeriod, setOnCurrentTime]);

    /**
     * State deselection handler
     */
    const deselectStatesAction = useCallback(() => {

        deselectStates();

    }, [deselectStates]);

    /**
     * Stop Propagation handler
     */
    const stopPropagationOnClick = useCallback((event) => {

        dispatch(FormActions.toggle(false));
        isMobile && dispatch(HmiPlayerActions.setRealTime(false));

        event.stopPropagation();

    }, [dispatch]);

    /**
     * Period picker by day
     */
    const periodSwitchCallback = useCallback((event)=>{

        event.preventDefault();

        // event.stopPropagation();

        setMinimapPeriodSelected(event.currentTarget.getAttribute('itemId'));

        togglePeriod(event.currentTarget.getAttribute('itemId'));

        if (['day', 'week', 'month', 'year', '2Hours', '12Hours'].includes(event.currentTarget.getAttribute('itemId'))) {
            setOnCurrentTime();
        }

    }, [setOnCurrentTime, togglePeriod]);

    /**
     * Time shift handler for the selected period
     */
    const shiftSelectionCallback = useCallback((event)=>{
        event.preventDefault();

        event.stopPropagation();

        if (event.currentTarget.getAttribute('itemId') === 'right') {
            if (selection && moment(minimapRange.endDate as Date).isAfter(moment())) {

                setOnCurrentTime();

            } else {

                setOffCurrentTime();

                realTimeHmiPlayer(false);

            }
        }

        if (event.currentTarget.getAttribute('itemId') === 'left') {

            setOffCurrentTime();

            realTimeHmiPlayer(false);
        }

        shiftSelection(minimapRange, event.currentTarget.getAttribute('itemId'), minimapPeriod);

    }, [setOnCurrentTime, shiftSelection, minimapRange, minimapPeriod, dashboardOnline, screenWidth, setOffCurrentTime, realTimeHmiPlayer, selection]);

    /**
     * Increase decrease handler for selected period
     */
    const zoomSelectionCallback = useCallback((event)=>{
        event.preventDefault();

        event.stopPropagation();

        zoomSelection(minimapRange, event.currentTarget.getAttribute('itemId'));

        setOffCurrentTime();

        realTimeHmiPlayer(false);

    }, [setOffCurrentTime, zoomSelection, minimapRange, dashboardOnline, screenWidth, realTimeHmiPlayer]);


    /**
     * Close HMI menu handler
     */
    const closeHmiMenu = useCallback(() => {

        setHmiMenuAnchor(null);

        toggleHMI(true);
    }, [setHmiMenuAnchor, toggleHMI]);

    return (

        <div className="panel">
            <Scrollbar
                permanentTrackY
                noScrollY
                removeTracksWhenNotUsed
                height={30}
                width={'100%'}
            >
                <List
                    style={{
                        display: 'flex',
                        minWidth: 'auto',
                        flexDirection: 'row',
                        justifyContent: 'normal',
                        height: 40,
                    }}
                    disablePadding
                    onClick={deselectStatesAction}
                >
                    <ListItem component="li" className="histogram-item">
                        <FormControl onClick={stopPropagationOnClick}>
                            <RadioGroup defaultValue="high" aria-label="histogram" name="histogram">
                                <Radio
                                    value="small"
                                    name="histogram"
                                    checked={histogramHeightSmall}
                                    // onChange={histogramHeightSwitch}
                                    onClick={histogramHeightSwitch}
                                    color="default"
                                    icon={<HistogramHighActive />}
                                    checkedIcon={<HistogramLowActive />}
                                />
                            </RadioGroup>
                        </FormControl>
                    </ListItem>
                    <ListItem component="li" className="datebox-item">
                        <DateRangePicker />
                    </ListItem>
                    <ListItem component="li" className="minimap-nav-item">
                        <List
                            style={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                            }}
                            disablePadding
                        >
                            <ListItem
                                onClick={stopPropagationOnClick}
                            >
                                <IconButton
                                    color="primary" component="span"
                                    itemID="left"
                                    onClick={shiftSelectionCallback}
                                >
                                    <NavigateBeforeIcon />
                                </IconButton>
                            </ListItem>
                            {/*{mainIntervals.twoHours?*/}
                            {/*    <ListItem*/}
                            {/*        className={minimapPeriod === '2Hours' ? 'minimap-nav-range-item active' : 'minimap-nav-range-item'}*/}
                            {/*        itemID="2Hours"*/}
                            {/*        onClick={periodSwitchCallback}*/}
                            {/*    >*/}
                            {/*        <span>{t('TWO_HOURS')}</span>*/}
                            {/*    </ListItem>*/}
                            {/*    : null*/}
                            {/*}*/}
                            {mainIntervals.twelveHours?
                                <ListItem
                                    className={minimapPeriodSelected === '12Hours' ? 'minimap-nav-range-item active' : 'minimap-nav-range-item'}
                                    itemID="12Hours"
                                    onClick={periodSwitchCallback}
                                >
                                    <span>{t('TWELVE_HOURS')}</span>
                                </ListItem>
                                : null
                            }
                            {mainIntervals.day?
                                <ListItem
                                    className={minimapPeriodSelected === 'day' ? 'minimap-nav-range-item active' : 'minimap-nav-range-item'}
                                    itemID="day"
                                    onClick={periodSwitchCallback}
                                >
                                    <span>{t('DAY')}</span>
                                </ListItem>
                                : null
                            }
                            {mainIntervals.week?
                                <ListItem
                                    className={minimapPeriodSelected === 'week' ? 'minimap-nav-range-item active' : 'minimap-nav-range-item'}
                                    itemID="week"
                                    onClick={periodSwitchCallback}
                                >
                                    <span>{t('WEEK')}</span>
                                </ListItem>
                                : null
                            }
                            {mainIntervals.month?
                                <ListItem
                                    className={minimapPeriodSelected === 'month' ? 'minimap-nav-range-item active' : 'minimap-nav-range-item'}
                                    itemID="month"
                                    onClick={periodSwitchCallback}
                                >
                                    <span>{t('MONTH')}</span>
                                </ListItem>
                                : null
                            }
                            {mainIntervals.year && !appConfig.demoAccount?
                                <ListItem
                                    className={minimapPeriodSelected === 'year' ? 'minimap-nav-range-item active' : 'minimap-nav-range-item'}
                                    itemID="year"
                                    onClick={periodSwitchCallback}
                                >
                                    <span>{t('YEAR')}</span>
                                </ListItem>
                                : null
                            }
                            <ListItem>
                                <IconButton color="primary" component="span"
                                            itemID="right"
                                            onClick={shiftSelectionCallback}
                                >
                                    <NavigateNextIcon />
                                </IconButton>
                            </ListItem>
                        </List>
                    </ListItem>
                    <ListItem component="li" className="zoom-item">
                        <List
                            style={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                            }}
                            disablePadding
                        >
                            <ListItem onClick={stopPropagationOnClick}>
                                <IconButton color="primary" component="span"
                                            itemID="zoomOut"
                                            onClick={zoomSelectionCallback}
                                >
                                    <ZoomOut />
                                </IconButton>
                            </ListItem>
                            <ListItem onClick={stopPropagationOnClick}>
                                <IconButton color="primary" component="span"
                                            itemID="zoomIn"
                                            onClick={zoomSelectionCallback}
                                >
                                    <ZoomIn />
                                </IconButton>
                            </ListItem>
                        </List>
                    </ListItem>

                        <ListItem component="li" className={`hmi-item ${(hmiVisible && Boolean(hmiMenuAnchor)) ? 'check' : ''}`}>
                            {enableHmi ?
                                <>
                                    <FormControlLabel
                                        ref={hmiBtnRef}
                                        className="field-checkbox"
                                        onClick={stopPropagationOnClick}
                                        control={
                                            <Checkbox
                                                value="hmi"
                                                name="showHmi"
                                                checked={hmiVisible || Boolean(hmiMenuAnchor)}
                                                onChange={hmiSwitch}
                                                color="primary"
                                                icon={<span className="checkbox-icon" />}
                                            />
                                        }
                                        label={t('SHOW_HMI')}
                                    />
                                    <DropdownArrowIcon />
                                    <ButtonMenu
                                        anchorEl={hmiMenuAnchor}
                                        open={Boolean(hmiMenuAnchor)}
                                        handleClose={closeHmiMenu}
                                        onClose={closeHmiMenu}
                                    />
                                </>
                                : null}
                        </ListItem>
                </List>
            </Scrollbar>
            <PanelMenu />
            {enableHmi?
                <Player />
                :
                null
            }
        </div>
    );
};

/**
 * Map global state to component props
 *
 * @param {Object} state
 *
 * @return {Object}
 */
const mapStateToProps = (state: any) => {

    const {
        graphHistogramHeight,
        graphMinimapVisibility,
        graphPeriod,
        graphHistogramMode,
        graphBarHeight,
        graphBarDescriptionVisibility,
        graphHMIVisibility,
        dashboard,
        dashboardSelect,
        graphMinimapBrush,
        appSetting,
    } = state;

    const mainIntervals = selectMainIntervals(state as RootState);

    return {
        histogramHeight: graphHistogramHeight.height,
        histogramMode: graphHistogramMode.mode,
        minimapVisible: graphMinimapVisibility.visible,
        minimapPeriod: graphPeriod.currentPeriod,
        minimapScaleDates: graphPeriod.scaleDates,
        minimapRange: graphPeriod.range,
        barHeight: graphBarHeight.height,
        barDescriptionVisible: graphBarDescriptionVisibility.visible,
        dashboardOnline: dashboard.online,
        screenWidth: dashboard.screenWidth,
        selection: graphMinimapBrush.selection,
        defaultMinimapPeriod: appSetting.settings.defaultMonitoringView,
        selectedDashboard: dashboardSelect.selectedDashboard,
        hmiVisible: graphHMIVisibility.visible,
        enableHmi: appSetting.settings.hmi.isEnabled,
        mainIntervals,
    };
};

/**
 * Map dispatch to component props
 *
 * @type {object}
 */
const mapDispatchToProps = ({
    histogramToggleHeight: GraphActions.histogramToggleHeight,
    histogramSwitchMode: GraphActions.histogramSwitchMode,
    minimapToggle: GraphActions.minimapToggle,
    togglePeriod: GraphActions.periodSelect,
    zoomSelection: GraphActions.zoomSelect,
    barToggleHeight: GraphActions.barToggleHeight,
    barToggleDescription: GraphActions.barToggleDescription,
    shiftSelection: GraphActions.shiftSelection,
    deselectStates: statesActions.deselectAllStates,
    barToggleTableView: GraphActions.barToggleTableView,
    setOffCurrentTime: DashboardActions.setOffCurrentTime,
    setOnCurrentTime: DashboardActions.setOnCurrentTime,
    exportAllState: ExportDataActions.exportAllState,
    toggleHMI: GraphActions.toggleHMI,
    setHmiSchema: HmiPlayerActions.setSchema,
    stopHmiPlayer: HmiPlayerActions.stop,
    realTimeHmiPlayer: HmiPlayerActions.setRealTime,
});

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(Panel));
